/**
 * useFeatureFlag — Merkezi feature flag hook + context.
 *
 * Şub 16, 2026 — Backend `db.feature_flags` collection'ından tek çağrıda
 * (`GET /api/feature-flags/public`) tüm flag'leri çekip in-memory cache
 * ediyoruz. Herhangi bir component tek satırla kontrol edebilir:
 *
 *   const isEnabled = useFeatureFlag('employee_self_finance');
 *   if (!isEnabled) return <Navigate to="/dashboard" />;
 *
 * State kaynağı: Provider bir kere GET yapar; admin sayfası flag toggle
 * ettiğinde `refresh()` ile yeniden çeker. `BroadcastChannel` ile sekmeler
 * arası senkron (opsiyonel).
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';
import { useAuthStore } from './auth';

const FeatureFlagsContext = createContext({
  flags: {},
  loading: true,
  isEnabled: () => false,
  refresh: () => {},
});

const CHANNEL_NAME = 'feature-flags-sync';
let bc = null;

function getChannel() {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null;
  if (!bc) {
    try { bc = new BroadcastChannel(CHANNEL_NAME); } catch { bc = null; }
  }
  return bc;
}

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);

  const load = useCallback(async () => {
    // Auth yoksa flag'leri yüklemeye çalışma (endpoint 401 döner)
    if (!user) { setFlags({}); setLoading(false); return; }
    try {
      const res = await api.get('/feature-flags/public');
      setFlags(res.data?.data || {});
    } catch {
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refresh = useCallback(async () => {
    await load();
    try { getChannel()?.postMessage({ type: 'refresh' }); } catch { /* ignore */ }
  }, [load]);

  useEffect(() => {
    load();
    const ch = getChannel();
    if (!ch) return;
    const handler = (ev) => {
      if (ev?.data?.type === 'refresh') load();
    };
    ch.addEventListener('message', handler);
    return () => ch.removeEventListener('message', handler);
  }, [load]);

  const isEnabled = useCallback((key) => !!flags[key], [flags]);

  const value = { flags, loading, isEnabled, refresh };
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

/** Tek bir flag'in enabled durumunu döner. */
export function useFeatureFlag(key) {
  const ctx = useContext(FeatureFlagsContext);
  return ctx.isEnabled(key);
}

/** Tüm flags + loading + refresh. */
export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
